import type {
  CatalogProduct,
  CatalogProductVariant,
  CatalogProductPurchasabilityReason,
  CatalogProductStockPolicy,
} from '../../types'
import { StorefrontProductDetailsPanelShell } from '@emporion/ui'
import ProductPurchaseCard from './ProductPurchaseCard'
import { ProductVariantSelector } from './ProductVariantSelector'

type Props = {
  productData: CatalogProduct
  formatCurrency: (value: number) => string
  priceExVat: number
  priceIncVat: number
  cartQuantity: number
  addToCartPending: boolean
  addToCartMessage: string
  addToCartMessageTone: 'success' | 'error' | null
  isPurchasable: boolean
  purchasabilityReason: CatalogProductPurchasabilityReason | null
  stockPolicy: CatalogProductStockPolicy
  availableStock: number
  onIncreaseCartQuantity: () => void
  onDecreaseCartQuantity: () => void
  onAddToCart: () => void
  productVariants: CatalogProductVariant[]
  selectedVariantKey: string
  onSelectProductVariant: (variantKey: string) => void
  onShareProduct?: () => void
  disableShareAction?: boolean
}

export const ProductDetailsPanel = ({
  productData,
  formatCurrency,
  priceExVat,
  priceIncVat,
  cartQuantity,
  addToCartPending,
  addToCartMessage,
  addToCartMessageTone,
  isPurchasable,
  purchasabilityReason,
  stockPolicy,
  availableStock,
  onIncreaseCartQuantity,
  onDecreaseCartQuantity,
  onAddToCart,
  productVariants,
  selectedVariantKey,
  onSelectProductVariant,
  onShareProduct,
  disableShareAction = false,
}: Props) => {
  const disableQuantityControls = addToCartPending || !isPurchasable
  const isOutOfStock = !isPurchasable && purchasabilityReason === 'out_of_stock'
  const exceedsAvailableStock = stockPolicy === 'deny-oversell' && availableStock > 0 && cartQuantity > availableStock
  const disableAddToCart =
    addToCartPending || !isPurchasable || (stockPolicy === 'deny-oversell' && (availableStock <= 0 || exceedsAvailableStock))
  const addToCartLabel = addToCartPending
    ? 'Aggiunta in corso...'
    : isOutOfStock
    ? 'Esaurito'
    : exceedsAvailableStock
    ? 'Disponibilita cambiata'
    : isPurchasable
    ? 'Aggiungi al carrello'
    : 'Non acquistabile'
  const availabilityLabel =
    isOutOfStock
      ? 'Esaurito'
      : stockPolicy === 'allow-backorder'
      ? 'Disponibile su ordinazione'
      : availableStock <= 3
      ? `Solo ${availableStock} disponibili`
      : 'Disponibile'
  const availabilityTone =
    isOutOfStock
      ? 'critical'
      : stockPolicy === 'allow-backorder'
      ? 'info'
      : availableStock <= 3
      ? 'warn'
      : 'ok'

  const availabilityText =
    stockPolicy === 'allow-backorder'
      ? `Disponibilita immediata: ${availableStock} pezzi, ordini extra gestiti in backorder.`
      : isOutOfStock
      ? 'Questa configurazione non e disponibile a magazzino.'
      : exceedsAvailableStock
      ? `La quantita selezionata supera la disponibilita attuale (${availableStock}).`
      : `Disponibilita a magazzino: ${availableStock} pezzi.`

  return (
    <StorefrontProductDetailsPanelShell
      productName={productData.name}
      variantSelector={
        <ProductVariantSelector
          productData={productData}
          variants={productVariants}
          selectedVariantKey={selectedVariantKey}
          onSelectVariant={onSelectProductVariant}
        />
      }
      purchaseCard={
        <ProductPurchaseCard
          formatCurrency={formatCurrency}
          priceExVat={priceExVat}
          priceIncVat={priceIncVat}
          availabilityLabel={availabilityLabel}
          availabilityTone={availabilityTone}
          availabilityText={availabilityText}
          cartQuantity={cartQuantity}
          disableQuantityControls={disableQuantityControls}
          disableAddToCart={disableAddToCart}
          addToCartLabel={addToCartLabel}
          addToCartMessage={addToCartMessage}
          addToCartMessageTone={addToCartMessageTone}
          onIncreaseCartQuantity={onIncreaseCartQuantity}
          onDecreaseCartQuantity={onDecreaseCartQuantity}
          onAddToCart={onAddToCart}
          onShareProduct={onShareProduct}
          disableShareAction={disableShareAction}
        />
      }
    />
  )
}
